import { useEffect, useRef, useState } from 'react';
import { useConfig } from '../../../config';
import { useTranslation } from '../../../i18n/i18n';
import { FilterContainer } from '../../../state/filters';
import { ApiFacetLoop54, FacetItem } from '../../../types';
import { filter_badge_close, Icon } from '../../Icon/Icon';
import './FilterBadges.css';

export const FilterBadges = ({ hits, perma }: { hits?: number; perma: string }) => {
	const t = useTranslation().filtering;
	const {
		getAppliedFilters,
		resetFilters,
		getNumOfAppliedFilters,
		getUnitFromAttributeString,
		getAttributeNameFromAttributeString,
		isRangeFacetApplied,
		getRangeSelectionForFacet,
		updateFilters,
	} = FilterContainer.useContainer();
	const [displayedHits, setDisplayedHits] = useState<number>(hits || 0);
	const { currency } = useConfig();
	const appliedFilters = getAppliedFilters();
	const prevPermaRef = useRef<string>(perma);
	const prevHitsRef = useRef<number>(hits || 0);
	const [justMounted, setJustMounted] = useState(true);
	const [firstHitsUpdateDone, setFirstHitsUpdateDone] = useState(false);
	const [justNavigated, setJustNavigated] = useState(false);

	useEffect(() => {
		if (prevPermaRef.current !== perma) {
			setDisplayedHits(prevHitsRef.current);
			setJustNavigated(true);
		} else {
			setJustNavigated(false);
		}

		prevPermaRef.current = perma;

		if (justMounted) {
			setDisplayedHits(hits || 0);
			setJustMounted(false);
		}
	}, [perma, hits]);

	useEffect(() => {
		if (hits !== undefined) {
			prevHitsRef.current = hits;
		}

		if ((justMounted || justNavigated || !firstHitsUpdateDone) && hits !== undefined) {
			setDisplayedHits(hits);
			setFirstHitsUpdateDone(true);
		} else if (!justNavigated && !justMounted && firstHitsUpdateDone && hits !== undefined) {
			const duration = 200;
			const intervalTime = 20;
			const totalSteps = duration / intervalTime;
			const diff = hits - displayedHits;

			let increment: number;
			if (Math.abs(diff) > 100) {
				increment = diff * 0.05;
			} else {
				increment = diff / totalSteps;
			}

			let currentHits = displayedHits;
			let step = 0;

			const interval = setInterval(() => {
				step += 1;
				currentHits += increment;

				if (step >= totalSteps || Math.abs(currentHits - hits) < Math.abs(increment)) {
					clearInterval(interval);
					setDisplayedHits(hits);
				} else {
					setDisplayedHits(Math.round(currentHits));
				}
			}, intervalTime);

			return () => clearInterval(interval);
		}
	}, [hits, justNavigated, justMounted, firstHitsUpdateDone, displayedHits]);

	type RangeFacet = Extract<ApiFacetLoop54, { type: 'range' }>;

	const handleOnCloseRangeBadge = (facet: RangeFacet) => {
		updateFilters(facet.name, [undefined, undefined], false);
	};

	return (
		<div id="filter-badges">
			<div className="articles-text">
				<h2>
					{displayedHits} {t.articles}
				</h2>
			</div>
			{appliedFilters.map((f) => {
				const normalizedFacetName = getAttributeNameFromAttributeString(f.name);

				if (f.type === 'distinct') {
					return f.items
						?.filter((i) => i.selected)
						.map((i) => (
							<FilterBadge
								facetName={f.name}
								facetDisplayName={f.displayName ?? normalizedFacetName}
								displayName={f.displayName}
								facetItem={i}
								key={f.name + i.item}
							/>
						));
				} else if (f.type === 'range') {
					if (!isRangeFacetApplied(f)) {
						return null;
					}

					const rangeSelection = getRangeSelectionForFacet(f);
					if (!rangeSelection) {
						return null;
					}

					const hasSelectedMin = f.selectedMin !== undefined && f.selectedMin !== null;
					const hasSelectedMax = f.selectedMax !== undefined && f.selectedMax !== null;
					const unit = f.name === 'price' ? currency : getUnitFromAttributeString(f.name);
					let rangeLabel: string;

					if (hasSelectedMin && hasSelectedMax) {
						rangeLabel = `${String(f.selectedMin)} - ${String(f.selectedMax)}`;
					} else if (hasSelectedMin) {
						rangeLabel = `>= ${String(f.selectedMin)}`;
					} else {
						rangeLabel = `<= ${String(f.selectedMax)}`;
					}

					const displayLabel = unit ? `${rangeLabel} ${unit}` : rangeLabel;

					return (
						<FilterBadge
							facetName={f.name}
							displayName={displayLabel}
							facetItem={{ count: 0, selected: true, item: '' }}
							key={f.name + String(f.selectedMin) + String(f.selectedMax)}
							closeHandler={() => handleOnCloseRangeBadge(f)}
						/>
					);
				}
			})}
			{getNumOfAppliedFilters() > 1 && (
				<div className="clear-text">
					<p onClick={() => resetFilters()}>{t.clear}</p>
				</div>
			)}
		</div>
	);
};

const FilterBadge = ({
	facetName,
	displayName,
	facetDisplayName,
	facetItem,
	closeHandler,
}: {
	facetName: string;
	displayName?: string;
	facetDisplayName?: string;
	facetItem: FacetItem;
	closeHandler?: () => void;
}) => {
	const { updateFilters } = FilterContainer.useContainer();
	const { getUnitFromAttributeString } = FilterContainer.useContainer();
	const t = useTranslation().filtering;

	const handleOnClose = () => {
		if (!closeHandler) {
			updateFilters(facetName, facetItem.item, false);
		} else {
			closeHandler();
		}
	};
	const facetUnit = getUnitFromAttributeString(facetName);
	const facetLabel = facetDisplayName ?? facetName;
	const rawValue = `${facetItem.item ?? ''}`.trim();

	const normalized = (value?: string) => (value ? value.trim().toLocaleLowerCase() : undefined);
	const normalizedValue = normalized(rawValue);
	const normalizedYes = normalized(t.booleanYes);
	const normalizedNo = normalized(t.booleanNo);

	const matchesYes = Boolean(normalizedValue && normalizedYes && normalizedValue === normalizedYes);
	const matchesNo = Boolean(normalizedValue && normalizedNo && normalizedValue === normalizedNo);
	const localizedBooleanValue = matchesYes ? t.booleanYes : matchesNo ? t.booleanNo : null;

	const shouldShowFacetWithBoolean = !displayName && Boolean(facetLabel) && Boolean(localizedBooleanValue);
	const booleanLabel =
		shouldShowFacetWithBoolean && localizedBooleanValue ? `${facetLabel}: ${localizedBooleanValue}` : null;

	const defaultLabel = `${rawValue}${facetUnit ? ` ${facetUnit}` : ''}`.trim();
	const labelToRender = displayName || booleanLabel || defaultLabel;

	return (
		<div className="filter-badge-wrapper">
			<div className="filter-badge">
				<span>
					<p>{labelToRender}</p>
					<span className="filter-badge-close-icon" onClick={handleOnClose}>
						<Icon shape={filter_badge_close} />
					</span>
				</span>
			</div>
		</div>
	);
};
